import { Injectable } from '@angular/core';

export type LoginIssuer = 'entra' | 'auth0' | 'internal';

const STORAGE_KEY = 'ups.login.issuer';
const HINT_STORAGE_KEY = 'ups.login.hint';

/**
 * Boot-time issuer selection shared by the AuthenticationService binding,
 * the AuthFacade, and the issuer-aware HTTP interceptor (#29709). Entra
 * wins when it was the last-used issuer, or when nothing is persisted but
 * MSAL already holds a session (e.g. storage cleared while the MSAL cache
 * survived). Auth0 is the default otherwise — every pre-existing user
 * signs in there today. Pure so the rule is testable without an injector.
 * (Lives here, not in entra-msal.providers, to keep the interceptor's
 * import chain cycle-free; the providers file re-exports it.)
 *
 * 🔴 **LOAD-BEARING PRECONDITION (#30531): a persisted 'entra' means a
 * redirect COMPLETED, never that one was merely attempted.** This function
 * ignores `msalAccountCount` when the marker says 'entra', and that is
 * deliberate: on the boot returning from Microsoft the account can land
 * after this runs, so requiring a non-zero count here would bounce a
 * freshly signed-in user to Auth0 (the failure the count-ignoring rule was
 * written to prevent). The safety therefore lives in the WRITER, not here.
 * `DualLoginUiService.loginWithEntra` used to write the marker before
 * dispatching the redirect, which broke the precondition and left failed
 * logins asserting an Entra session with no account; the only writer is now
 * the `APP_INITIALIZER` in entra-msal.providers, on redirect success.
 *
 * ⚠️ So do NOT "harden" this by demanding an account when persisted is
 * 'entra'. That reintroduces the bounce. Keep the invariant at the write.
 */
export function selectLoginIssuer(
  persisted: LoginIssuer | null,
  msalAccountCount: number
): LoginIssuer {
  if (persisted === 'entra') return 'entra';
  if (persisted === null && msalAccountCount > 0) return 'entra';
  return 'auth0';
}

/**
 * Remembers which issuer the user last signed in with, so app boot can
 * bind the matching {@link AuthenticationService} implementation and
 * returning users skip realm discovery. Redirect logins reboot the app,
 * so the persisted value is read exactly once per boot by the
 * issuer-aware binding in `buildEntraMsalProviders`.
 */
@Injectable({ providedIn: 'root' })
export class LoginIssuerStore {
  get(): LoginIssuer | null {
    if (typeof window === 'undefined') return null;
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'entra' || value === 'auth0' || value === 'internal'
      ? value
      : null;
  }

  set(issuer: LoginIssuer): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, issuer);
  }

  /**
   * Wipes issuer AND hint together: "Use a different account" calls this, and
   * a surviving hint would silently re-hint the previous person's identity to
   * the next user of a shared terminal (WI 31157).
   */
  clear(): void {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(HINT_STORAGE_KEY);
  }

  /**
   * The identity that last COMPLETED an Entra redirect (WI 31157). Written
   * only by the same on-success hook that writes the issuer marker
   * (`completeEntraRedirectBoot`), so the #30531 invariant extends here: a
   * persisted hint means a redirect finished as this user, never that one was
   * merely attempted. Ridden as loginHint on the returning-user path so Entra
   * silently SSOs the live server session instead of forcing an account
   * picker on every session start.
   */
  getHint(): string | null {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(HINT_STORAGE_KEY);
  }

  setHint(username: string): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(HINT_STORAGE_KEY, username);
  }
}

/**
 * Account count that survives a pre-initialize read (WI 31092). msal-browser
 * v4+ throws uninitialized_public_client_application when accounts are read
 * before initialize() settles, and every consumer of this count (the
 * issuer-selection provider factory, AuthFacade.issuer's field initializer)
 * runs at module construction, i.e. pre-init. With the per-tab sessionStorage
 * cache the pre-init read always saw an empty cache and never threw; the
 * persisted localStorage cache holds accounts on every RETURNING boot, which
 * is exactly when the unguarded read crashes bootstrap (the Roost Electron
 * shell hit this live, 2026-08). Falling back to 0 is safe: a returning Entra
 * session also has the persisted issuer marker, which selects 'entra' on its
 * own. Lives here, not in entra-msal.providers, for the same cycle-free-import
 * reason as selectLoginIssuer (AuthFacade cannot import the providers file:
 * providers -> IssuerAwareAuthInterceptor -> AuthFacade).
 */
export function accountCountSafe(instance: {
  getAllAccounts(): unknown[];
}): number {
  try {
    return instance.getAllAccounts().length;
  } catch {
    return 0;
  }
}
